import { useEffect, useId, useRef, useState } from "react";
import { useAuth } from "@/auth/AuthProvider";
import { WizardStepper } from "@/base/WizardStepper";
import { buttonClass } from "@/components/buttonStyles";
import { WonBidAwardStep } from "@/components/bond-submission/WonBidAwardStep";
import { WonBidFormsStep } from "@/components/bond-submission/WonBidFormsStep";
import { WonBidPriceStep } from "@/components/bond-submission/WonBidPriceStep";
import { WonBidResultsStep } from "@/components/bond-submission/WonBidResultsStep";
import type { HeldDocument } from "@/components/bond-submission/WonBidUploadSlot";
import { ApiError } from "@/lib/api";
import { getBondForms, type BondForms } from "@/lib/bondForms";
import { getAccount, type AccountProgramCapacity } from "@/lib/brokerAccounts";
import {
  documentRejection,
  recordWonBid,
  uploadBondFinalContract,
  uploadWonBidDocument,
  FINAL_CONTRACT_MAX_BYTES,
  type BondDocument,
  type BondSubmission,
  type WonBidDocumentType,
  type WonBidInput,
  type WonBidResponse,
} from "@/lib/brokerBonds";
import {
  acceptAttribute,
  documentRuleMessage,
  screenDocument,
} from "@/lib/brokerDocumentTypes";
import { messageFor } from "@/lib/errors";
import { performancePremium } from "@/lib/premium";
import { wonFiguresUnsaved } from "@/lib/spreadLetter";
import { newestDocumentOf, useBondDocuments } from "@/lib/useBondDocuments";
import { useDocumentTypes } from "@/lib/useDocumentTypes";
import {
  bidFigureErrors,
  bondsRequestedFrom,
  estimateFromOrder,
  includesPayment,
  orderCapacityHold,
  priceChange,
  priceChangeNeedsNote,
  spreadLetterRequired,
  wonBidRefusal,
  wonCapacityPreview,
  wonStepBlockers,
  wonStepOfField,
  SAVE_BEFORE_LETTER,
  WON_BID_STEPS,
} from "@/lib/wonBid";

// BRK-S421 — the won bid as the wireframe's four steps: Final price, Bid
// results, Confirm bond forms, Award letter. ONE component behind both
// entries: the Bid Result dialog's Won (`WonBidStepperDialog`) and the
// `/surety/submissions/:id/won` route (`WonBidPage`), so deep links and the
// reminders keep landing on the same flow.
//
// It re-lays BRK-S184's screen without changing its rules:
//
// ── NOTHING HERE IS OPTIMISTIC (NN #7) ───────────────────────────────────────
//
// An upload's slot fills only from its 2xx (the server's document pointer), a
// bond form reads Confirmed only from the refreshed list its 2xx answers, and
// finishing is `recordWonBid`'s 2xx alone — the caller says so from the
// RESPONSE (`onRecorded`). A refusal keeps every value, and lands on the step
// that asks for the field it names (`wonStepOfField`).
//
// ── INFORMATION, NOT GATES ───────────────────────────────────────────────────
//
// The price-change note, the limit check and the spread-letter notice never
// hold a step. Each step waits only on what IT asks for (`wonStepBlockers`):
// the price on the first, consistent bid figures on the second, the award
// letter on the last, and any upload in flight.

/** The upload slots, keyed as the won body names their documents. */
type Slot = "bidResults" | "spreadLetter" | "awardLetter";

const SLOT_FIELD: Record<Slot, string> = {
  bidResults: "bidResultsDocumentId",
  spreadLetter: "spreadLetterDocumentId",
  awardLetter: "awardLetterDocumentId",
};

const SLOT_TYPE: Record<Slot, string> = {
  bidResults: "bid_results",
  spreadLetter: "bid_spread_letter",
  awardLetter: "final_contract",
};

const STEP_HEADINGS = [
  "Final contract price",
  "Bid results",
  "Confirm bond forms",
  "Award letter or contract",
] as const;

/** The newest document of `type` on the order, so a broker returning after a
 *  failed finish (or correcting a recorded win) finds their uploads in place. */
function latestOfType(
  documents: readonly BondDocument[] | null,
  type: string,
): HeldDocument | null {
  const doc = newestDocumentOf(documents, type);
  return doc ? { documentId: doc.documentId, filename: doc.filename } : null;
}

export function WonBidStepper({
  order,
  headingLevel = 2,
  onSaved,
  onRecorded,
}: {
  order: BondSubmission;
  /** The steps' heading level: 2 on the page, 3 inside the dialog. */
  headingLevel?: 2 | 3;
  /** A save that stays in the flow (the spread letter's): the order view the
   *  PUT answered becomes the caller's, so the recorded result is current. */
  onSaved: (order: BondSubmission) => void;
  /** The finish's 2xx, with the bonds the response (else the body) recorded. */
  onRecorded: (fresh: WonBidResponse, bondsRequested: string) => void;
}) {
  const { authedRequest, authedUpload, authedUploadWithProgress } = useAuth();
  const { rules } = useDocumentTypes(authedRequest);
  const { documents } = useBondDocuments(authedRequest, order.id);
  const reasonsId = useId();
  const stepHeadingId = useId();
  const Heading = headingLevel === 2 ? "h2" : "h3";
  const SubHeading = headingLevel === 2 ? "h3" : "h4";

  // The account block's capacity (BRK-S186) and the program rate (BRK-S328).
  // `undefined` while read; a failed read says nothing rather than guess (the
  // preview is a preview).
  const [capacity, setCapacity] = useState<
    AccountProgramCapacity | null | undefined
  >(undefined);
  const [accountRate, setAccountRate] = useState<number | null>(null);
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const account = await getAccount(authedRequest, order.accountId);
        if (cancelled) return;
        setCapacity(account.preapproval?.capacity ?? null);
        setAccountRate(account.preapproval?.terms?.ratePer1000 ?? null);
      } catch {
        if (!cancelled) setCapacity(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authedRequest, order.accountId]);

  // The bond forms preset for this bond (PMS-S848). A failed
  // read is said on its step and holds nothing.
  const [forms, setForms] = useState<BondForms | null>(null);
  const [formsError, setFormsError] = useState(false);
  const [formsBusy, setFormsBusy] = useState(false);
  useEffect(() => {
    let cancelled = false;
    getBondForms(authedRequest, order.id)
      .then((read) => {
        if (!cancelled) setForms(read);
      })
      .catch(() => {
        if (!cancelled) setFormsError(true);
      });
    return () => {
      cancelled = true;
    };
  }, [authedRequest, order.id]);

  // A recorded WIN being corrected opens on its own figures; anything else on
  // the estimate.
  const savedWon = order.bidResult?.outcome === "won" ? order.bidResult : null;
  const estimate = estimateFromOrder(order);
  const [step, setStep] = useState(0);
  const [finalPrice, setFinalPrice] = useState<number | undefined>(
    savedWon?.finalContractPrice ?? estimate ?? undefined,
  );
  const [nextClosest, setNextClosest] = useState<number | undefined>(
    savedWon?.nextClosestBidAmount ?? undefined,
  );
  const [secondClosest, setSecondClosest] = useState<number | undefined>(
    savedWon?.secondClosestBidAmount ?? undefined,
  );
  const [payment, setPayment] = useState(() =>
    includesPayment(savedWon?.bondsRequested),
  );
  const [held, setHeld] = useState<Record<Slot, HeldDocument | null>>({
    bidResults: null,
    spreadLetter: null,
    awardLetter: null,
  });
  const [uploading, setUploading] = useState<Record<Slot, boolean>>({
    bidResults: false,
    spreadLetter: false,
    awardLetter: false,
  });
  const [uploadErrors, setUploadErrors] = useState<
    Partial<Record<Slot, string>>
  >({});
  const [busy, setBusy] = useState(false);
  const [letterSaving, setLetterSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [serverInvalid, setServerInvalid] = useState<Record<string, string>>(
    {},
  );

  // The order's documents arrive after mount: fill each EMPTY slot once from
  // the newest of its type, never over one the broker just chose.
  const prefilled = useRef(false);
  useEffect(() => {
    if (prefilled.current || documents === null) return;
    prefilled.current = true;
    setHeld((prev) => ({
      bidResults:
        prev.bidResults ?? latestOfType(documents, SLOT_TYPE.bidResults),
      spreadLetter:
        prev.spreadLetter ?? latestOfType(documents, SLOT_TYPE.spreadLetter),
      awardLetter:
        prev.awardLetter ?? latestOfType(documents, SLOT_TYPE.awardLetter),
    }));
  }, [documents]);

  const figureErrors = bidFigureErrors(nextClosest, secondClosest);
  const spreadRequired = spreadLetterRequired(finalPrice, nextClosest);
  const bonds = bondsRequestedFrom(payment);
  const rate = order.program?.rate ?? accountRate;
  const facts = {
    finalPrice,
    figureErrors,
    awardLetter: held.awardLetter !== null,
    uploading: Object.values(uploading).some(Boolean) || formsBusy,
  };
  const reasons = wonStepBlockers(step, facts);
  // Every step's, for a save made before the last step (the letter's).
  const allReasons = WON_BID_STEPS.flatMap((_, i) => wonStepBlockers(i, facts));
  const last = step === WON_BID_STEPS.length - 1;
  const accept = rules ? acceptAttribute(rules) : "";

  const clearServer = (field: string) =>
    setServerInvalid((prev) => {
      const next = { ...prev };
      delete next[field];
      return next;
    });

  async function upload(slot: Slot, file: File) {
    setUploadErrors((prev) => ({ ...prev, [slot]: undefined }));
    clearServer(SLOT_FIELD[slot]);
    if (slot === "awardLetter") {
      // The final-contract route's own cap, restated so the broker learns
      // before the wait (the route is PDF only).
      if (file.size > FINAL_CONTRACT_MAX_BYTES) {
        setUploadErrors((prev) => ({
          ...prev,
          awardLetter: `That file is too large — please upload a PDF under ${String(FINAL_CONTRACT_MAX_BYTES / (1024 * 1024))} MB.`,
        }));
        return;
      }
    } else if (rules) {
      const rejection = screenDocument(file, rules);
      if (rejection) {
        setUploadErrors((prev) => ({ ...prev, [slot]: rejection.message }));
        return;
      }
    }
    setUploading((prev) => ({ ...prev, [slot]: true }));
    try {
      const stored: HeldDocument =
        slot === "awardLetter"
          ? await uploadBondFinalContract(authedUpload, order.id, file)
          : await uploadWonBidDocument(
              authedUploadWithProgress,
              order.id,
              file,
              SLOT_TYPE[slot] as WonBidDocumentType,
            );
      setHeld((prev) => ({
        ...prev,
        [slot]: { documentId: stored.documentId, filename: stored.filename },
      }));
    } catch (err) {
      const refused = documentRejection(err);
      setUploadErrors((prev) => ({
        ...prev,
        [slot]:
          refused && rules
            ? documentRuleMessage(refused.rule, refused.filename, rules)
            : err instanceof ApiError
              ? messageFor(err)
              : "We couldn’t upload that file. Please try again.",
      }));
    } finally {
      setUploading((prev) => ({ ...prev, [slot]: false }));
    }
  }

  /** The won save (PUT …/bid-result) — the finish's and the letter's.
   *  Resolves the response, or null after rendering why it did not save. */
  async function save(): Promise<WonBidResponse | null> {
    if (
      allReasons.length > 0 ||
      finalPrice === undefined ||
      held.awardLetter === null
    ) {
      return null;
    }
    const body: WonBidInput = {
      outcome: "won",
      finalContractPrice: finalPrice,
      bondsRequested: bonds,
      awardLetterDocumentId: held.awardLetter.documentId,
      ...(nextClosest !== undefined
        ? { nextClosestBidAmount: nextClosest }
        : {}),
      ...(secondClosest !== undefined
        ? { secondClosestBidAmount: secondClosest }
        : {}),
      ...(held.bidResults
        ? { bidResultsDocumentId: held.bidResults.documentId }
        : {}),
      // Sent only while the notice asks for it.
      ...(spreadRequired && held.spreadLetter
        ? { spreadLetterDocumentId: held.spreadLetter.documentId }
        : {}),
    };
    setError(null);
    setServerInvalid({});
    try {
      return await recordWonBid(authedRequest, order.id, body);
    } catch (err) {
      // Every value stays; the refusal goes back to the step that asks for
      // the field it names, and its summary shows wherever the broker is.
      const refusal = wonBidRefusal(err);
      setServerInvalid(refusal.byField);
      setError(refusal.summary);
      const steps = Object.keys(refusal.byField)
        .map(wonStepOfField)
        .filter((s): s is number => s !== null);
      if (steps.length > 0) setStep(Math.min(...steps));
      return null;
    }
  }

  async function finish() {
    if (reasons.length > 0) return;
    setBusy(true);
    const fresh = await save();
    setBusy(false);
    if (fresh === null) return;
    const recorded = fresh.bidResult?.bondsRequested;
    onRecorded(fresh, typeof recorded === "string" ? recorded : bonds);
  }

  // The letter is filled from the RECORDED won result, so once one exists a
  // figure changed here must be saved before the letter goes (BRK-S331).
  const letterFigures = {
    finalContractPrice: finalPrice,
    nextClosestBidAmount: nextClosest,
  };
  const letterNeedsSave = wonFiguresUnsaved(order.bidResult, letterFigures);

  async function saveBeforeLetter(): Promise<boolean> {
    if (allReasons.length > 0) {
      setError(SAVE_BEFORE_LETTER);
      return false;
    }
    setLetterSaving(true);
    const fresh = await save();
    setLetterSaving(false);
    if (fresh === null) return false;
    onSaved(fresh);
    return true;
  }

  function go(to: number) {
    setError(null);
    setStep(to);
  }

  const preview =
    capacity === undefined
      ? null
      : wonCapacityPreview(capacity, orderCapacityHold(order), finalPrice);

  return (
    <div className="flex min-w-0 flex-col gap-4">
      <WizardStepper
        titles={WON_BID_STEPS}
        current={step}
        done={WON_BID_STEPS.map((_, i) => i < step)}
      />
      <section
        aria-labelledby={stepHeadingId}
        className="flex min-w-0 flex-col gap-4 rounded-xl border border-brk-border bg-brk-panel p-4 sm:p-6"
      >
        <Heading id={stepHeadingId} className="text-heading text-brk-text">
          {STEP_HEADINGS[step]}
        </Heading>
        {step === 0 && (
          <WonBidPriceStep
            finalPrice={finalPrice}
            onPrice={(value) => {
              setFinalPrice(value);
              clearServer("finalContractPrice");
            }}
            error={serverInvalid.finalContractPrice}
            change={priceChange(estimate, finalPrice)}
            showNote={priceChangeNeedsNote(estimate, finalPrice)}
            // DISPLAY ONLY (BRK-S328): never sent.
            premium={performancePremium(rate, finalPrice)}
            rate={rate}
            preview={preview}
            accountId={order.accountId}
          />
        )}
        {step === 1 && (
          <WonBidResultsStep
            order={order}
            accept={accept}
            heading={SubHeading}
            bidResults={held.bidResults}
            spreadLetter={held.spreadLetter}
            uploading={uploading}
            errors={{
              bidResultsDocumentId:
                uploadErrors.bidResults ?? serverInvalid.bidResultsDocumentId,
              spreadLetterDocumentId:
                uploadErrors.spreadLetter ??
                serverInvalid.spreadLetterDocumentId,
              nextClosestBidAmount:
                figureErrors.nextClosestBidAmount ??
                serverInvalid.nextClosestBidAmount,
              secondClosestBidAmount:
                figureErrors.secondClosestBidAmount ??
                serverInvalid.secondClosestBidAmount,
            }}
            nextClosest={nextClosest}
            secondClosest={secondClosest}
            onNextClosest={(value) => {
              setNextClosest(value);
              clearServer("nextClosestBidAmount");
            }}
            onSecondClosest={(value) => {
              setSecondClosest(value);
              clearServer("secondClosestBidAmount");
            }}
            spreadRequired={spreadRequired}
            letterFigures={letterFigures}
            saveFirst={letterNeedsSave ? saveBeforeLetter : undefined}
            onUpload={(slot, file) => void upload(slot, file)}
          />
        )}
        {step === 2 && (
          <WonBidFormsStep
            orderId={order.id}
            heading={SubHeading}
            payment={payment}
            onPayment={(on) => {
              setPayment(on);
              clearServer("bondsRequested");
            }}
            bondsError={serverInvalid.bondsRequested}
            forms={forms}
            formsError={formsError}
            rules={rules}
            accept={accept}
            onForms={setForms}
            onBusy={setFormsBusy}
          />
        )}
        {step === 3 && (
          <WonBidAwardStep
            held={held.awardLetter}
            uploading={uploading.awardLetter}
            error={
              uploadErrors.awardLetter ?? serverInvalid.awardLetterDocumentId
            }
            onFile={(file) => void upload("awardLetter", file)}
          />
        )}
      </section>

      <div className="flex flex-col gap-3">
        {error && (
          <p role="alert" className="text-sm text-brk-danger">
            {error}
          </p>
        )}
        {reasons.length > 0 && (
          <ul
            id={reasonsId}
            className="flex flex-col gap-1 text-sm text-brk-dim"
          >
            {reasons.map((r) => (
              <li key={r}>{r}</li>
            ))}
          </ul>
        )}
        {/* Stacked at 375 (the forward button on top), in a row from sm. */}
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          {step > 0 && (
            <button
              type="button"
              onClick={() => go(step - 1)}
              disabled={busy || letterSaving}
              className={buttonClass({ variant: "secondary" })}
            >
              Back
            </button>
          )}
          <button
            type="button"
            onClick={() => (last ? void finish() : go(step + 1))}
            disabled={busy || letterSaving || reasons.length > 0}
            aria-describedby={reasons.length > 0 ? reasonsId : undefined}
            className={buttonClass()}
          >
            {last ? (busy ? "Submitting…" : "Submit won bid") : "Next"}
          </button>
        </div>
      </div>
    </div>
  );
}
